/**
 * Reset Password Action — Phase 7 (ADR 0003).
 *
 * Server-side validation now uses `resetPasswordSchema`. The same
 * schema can drive future form-layer RHF wiring (when reset-password
 * gets a client-side resolver).
 */

import type { ActionFunctionArgs } from 'react-router-dom';
import axios from 'axios';
import ENDPOINTS from '@/utils/endpoints';
import { resetPasswordSchema } from '@/modules/auth/schemas';
import { getFormDataObject, actionError, actionSuccess, transformApiError } from './helpers';

export async function resetPasswordAction({ request }: ActionFunctionArgs) {
  try {
    const data = await getFormDataObject(request);

    const parsed = resetPasswordSchema.safeParse({
      password: data.password,
      confirmPassword: data.confirmPassword,
      token: data.token,
      email: data.email,
    });
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      const errors: Record<string, string> = {};
      Object.entries(flat).forEach(([key, msgs]) => {
        if (msgs && msgs.length > 0 && msgs[0]) errors[key] = msgs[0];
      });
      // Token-only failures historically returned a top-level error
      // string (not a field map); keep that behaviour for
      // useActionData consumers that branch on `actionData.error`.
      if (errors.token && Object.keys(errors).length === 1) {
        return actionError(errors.token, 400);
      }
      return actionError(errors, 400);
    }

    const { password, confirmPassword, token, email } = parsed.data;

    const response = await axios.post(ENDPOINTS.RESET_PASSWORD, {
      token,
      email,
      password,
      password_confirmation: confirmPassword,
    });

    return actionSuccess(
      { submitted: true },
      response.data.message || 'Password reset successfully'
    );
  } catch (error) {
    console.error('Reset password action error:', error);

    const errorResponse = transformApiError(error);
    return actionError(errorResponse.errors ?? errorResponse.error ?? 'Password reset failed', 400);
  }
}
